import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";

import { parseJwt } from "@lib/browser-utils";
import { trackFirstEntry, trackLogout } from "@lib/event-trackers";
import { retryTransient } from "@lib/react-query/retry";
import { useContainer } from "@src/contexts/container-context";
import { useAccountUIStore } from "@src/stores/zustand/account-ui-store";
import {
  finishLoginFlow,
  handleAuthError,
  LOGIN_TYPES,
} from "@src/utils/login-utils";

export function useCurrentUser() {
  const { currentUserCache } = useContainer();
  const [user, setUser] = useState(() => currentUserCache.get());

  useEffect(() => {
    return currentUserCache.subscribe(() => {
      setUser(currentUserCache.get());
    });
  }, [currentUserCache]);

  return user;
}

/**
 * Hook to fetch user classroom courses
 */
export function useUserClassroomCourses(userId, options = {}) {
  const { userService } = useContainer();

  return useQuery({
    queryKey: ["userClassroomCourses", userId],
    queryFn: () => userService.getUserClassrooms(userId),
    enabled: !!userId && options.enabled !== false,
    ...options,
  });
}

/**
 * Hook for login mutation
 */
export function useLogin() {
  const { accountService } = useContainer();
  const { currentUserCache, tokenCache, appSettingsCache } = useContainer();

  return useMutation({
    mutationFn: ({ username, password }) =>
      accountService.login(username, password),
    onSuccess: (user) => {
      finishLoginFlow(
        user,
        { tokenCache, currentUserCache },
        {
          type: LOGIN_TYPES.NORMAL,
        }
      );
    },
    onError: (error) => {
      handleAuthError(error, {
        tokenCache,
        currentUserCache,
        appSettingsCache,
      });
    },
  });
}

/**
 * Hook for SSO login mutation
 */
export function useSsoLogin() {
  const { accountService } = useContainer();
  const { currentUserCache, tokenCache, appSettingsCache } = useContainer();

  return useMutation({
    // core-auth reports firstEntry once per user; a retry would get false.
    retry: false,
    mutationFn: ([token]) => {
      return accountService.ssoLogin(token).then((user) => ({
        ...user,
        token,
      }));
    },
    onSuccess: (user) => {
      finishLoginFlow(
        user,
        { tokenCache, currentUserCache },
        {
          type: LOGIN_TYPES.SSO,
        }
      );
      if (user.firstEntry === true) {
        trackFirstEntry(user);
      }
    },
    onError: (error) => {
      handleAuthError(error, {
        tokenCache,
        currentUserCache,
        appSettingsCache,
      });
    },
  });
}

/**
 * Hook for impersonate mutation
 */
export function useImpersonate() {
  const queryClient = useQueryClient();
  const { accountService } = useContainer();
  const { currentUserCache, tokenCache, appSettingsCache } = useContainer();
  const { setImpersonating, clearStore } = useAccountUIStore();

  return useMutation({
    mutationFn: (token) => {
      // Clear current user session before starting impersonation
      clearStore();
      const currentToken = parseJwt(token);
      tokenCache.set(token, { exp: currentToken.exp });
      setImpersonating(true);

      return accountService.impersonate().then((user) => ({
        ...user,
        token,
        impersonatorId: currentToken.impersonatorId,
      }));
    },
    onSuccess: (user) => {
      // Clear other queries
      queryClient.clear();
      finishLoginFlow(
        user,
        { tokenCache, currentUserCache },
        {
          type: LOGIN_TYPES.IMPERSONATE,
          impersonatorId: user.impersonatorId,
        }
      );
    },
    onError: (error) => {
      setImpersonating(false);
      handleAuthError(error, {
        tokenCache,
        currentUserCache,
        appSettingsCache,
      });
    },
  });
}

/**
 * Hook for claiming the session a set-password transfer token stands for.
 * core-platform records the first entry for a claim, so nothing is tracked here.
 */
export function useClaimTransferSession() {
  const queryClient = useQueryClient();
  const { accountService, currentUserCache, tokenCache, appSettingsCache } =
    useContainer();
  const { clearStore } = useAccountUIStore();

  return useMutation({
    // The token is single-use; a timed-out claim may have consumed it already.
    retry: false,
    mutationFn: (sessionId) => accountService.claimTransferSession(sessionId),
    onSuccess: (user) => {
      clearStore();
      queryClient.clear();
      finishLoginFlow(
        user,
        { tokenCache, currentUserCache },
        {
          type: LOGIN_TYPES.NORMAL,
        }
      );
    },
    onError: (error) => {
      handleAuthError(error, {
        tokenCache,
        currentUserCache,
        appSettingsCache,
      });
    },
  });
}

/**
 * Hook for sending a fresh set-password link
 */
export function useResendRecoveryCode() {
  const { accountService } = useContainer();

  return useMutation({
    retry: false,
    mutationFn: (email) => accountService.resendRecoveryCode(email),
  });
}

/**
 * Hook for send reset password mutation
 */
export function useSendResetPassword() {
  const { accountService } = useContainer();

  return useMutation({
    mutationFn: (payload) => accountService.sendResetPassword(payload),
  });
}

/**
 * Hook for reset password mutation
 */
export function useResetPassword() {
  const { accountService } = useContainer();
  const { token } = useAccountUIStore();

  return useMutation({
    mutationFn: (payload) =>
      accountService.resetPassword({ ...payload, token }),
  });
}

/**
 * Hook for validate reset password token (used in reset-password and create-account flows)
 * This is a helper hook that components can use directly with useMutation
 */
export function useValidateResetPassword() {
  const { accountService } = useContainer();
  return useMutation({
    retry: retryTransient,
    mutationFn: ({ email, token }) =>
      accountService.validateResetPassword(email, token),
  });
}

/**
 * Hook for reset password mutation (used in reset-password flow)
 * @param {Object} formData - Form data containing token and self_signup flag
 * @param {string} formData.token - Reset password token
 * @param {boolean} formData.self_signup - Whether this is a self-signup flow
 */
export function useResetPasswordMutation(formData) {
  const { accountService } = useContainer();

  return useMutation({
    retry: false,
    mutationFn: (payload) => {
      const values = {
        token: formData?.token || "",
        password: payload.password,
        selfSignup: formData?.self_signup || false,
      };
      if (payload.termsAndConditionsVersion) {
        values.termsAndConditionsVersion = payload.termsAndConditionsVersion;
      }
      return accountService.resetPassword(values);
    },
  });
}

/**
 * Hook for create account mutation
 * @param {Object} formData - Form data containing token and self_signup flag
 * @param {string} formData.token - Create account token
 * @param {boolean} formData.self_signup - Whether this is a self-signup flow
 */
export function useCreateAccountMutation(formData) {
  const { accountService } = useContainer();

  return useMutation({
    retry: false,
    mutationFn: (payload) => {
      return accountService.resetPassword({
        token: formData?.token || "",
        password: payload.password,
        selfSignup: formData?.self_signup || false,
        ...(payload.termsAndConditionsVersion && {
          termsAndConditionsVersion: payload.termsAndConditionsVersion,
        }),
      });
    },
  });
}

/**
 * Hook for self signup mutation
 */
export function useSelfSignup() {
  const queryClient = useQueryClient();
  const { accountService } = useContainer();
  const { currentUserCache, tokenCache, appSettingsCache } = useContainer();
  const { setSelfSignupData } = useAccountUIStore();

  return useMutation({
    mutationFn: (token) => {
      const currentToken = parseJwt(token);
      tokenCache.set(token, { exp: currentToken.exp });
      return accountService.verify_self_signup();
    },
    onSuccess: (response) => {
      queryClient.clear();
      setSelfSignupData(response);
    },
    onError: (error) => {
      setSelfSignupData();
      handleAuthError(error, {
        tokenCache,
        currentUserCache,
        appSettingsCache,
      });
    },
  });
}

/**
 * Hook for ProProfs authorization mutation
 */
export function useProProfsAuthorization() {
  const { accountService } = useContainer();
  const { setRedirectUrl } = useAccountUIStore();

  return useMutation({
    mutationFn: ({ email, sourceDomain }) =>
      accountService.proprofsAuth({ userEmail: email, sourceDomain }),
    onSuccess: (response) => {
      setRedirectUrl(response);
    },
    onError: (error) => {
      console.error(error);
    },
  });
}

/**
 * Hook for logout
 */
export function useLogout() {
  const { accountService, currentUserCache, proProfsCache } = useContainer();
  const { clearStore, setLogOutRequested, doProProfsLogout } =
    useAccountUIStore();
  const queryClient = useQueryClient();

  return async (payload) => {
    const currentUser = currentUserCache.get();
    const { isSso } = currentUser || {};

    // Need to determine if we are logging out of SSO based on the IdP configuration
    let sloUrl = "";
    if (isSso) {
      sloUrl = await accountService.buildSloUrl();
    }

    // Clear React Query cache
    queryClient.clear();

    // Clear stores
    clearStore();

    Cookies.remove("socket_identity");

    const proProfsSettings = proProfsCache.get();
    const ssoSource = proProfsSettings?.ssoSourceUrl;

    if (payload && payload.logOutRequested) {
      setLogOutRequested(true);
    }
    if (ssoSource && !isSso) {
      doProProfsLogout(ssoSource.url);
    }

    if (sloUrl) {
      window.location.href = sloUrl;
    }

    trackLogout(payload?.logoutType);
  };
}
